import axios from 'axios';

// Use relative URL to leverage Vite proxy in development
const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  logout: () =>
    api.post('/auth/logout'),
  refreshToken: (refresh_token) =>
    api.post('/auth/refresh', { refresh_token }),
  resetPassword: (email) =>
    api.post('/auth/reset-password', { email }),
  verifyResetToken: (token) =>
    api.post('/auth/verify-reset-token', { token }),
  resetPasswordConfirm: (token, new_password) =>
    api.post('/auth/reset-password-confirm', { token, new_password }),
  changePassword: (access_token, new_password) =>
    api.post('/auth/change-password', { access_token, new_password }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Folders API
export const foldersAPI = {
  getAll: () => api.get('/folders'),
  getById: (id) => api.get(`/folders/${id}`),
  create: (data) => api.post('/folders', data),
  update: (id, data) => api.put(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  getPermissions: (id) => api.get(`/folders/${id}/permissions`),
  grantPermission: (id, data) => api.post(`/folders/${id}/permissions`, data),
  revokePermission: (id, userId) => api.delete(`/folders/${id}/permissions/${userId}`),
  getRolePermissions: (id) => api.get(`/folders/${id}/role-permissions`),
  setRolePermissions: (id, data) => api.put(`/folders/${id}/role-permissions`, data),
  propagateRolePermissions: (id, data) => api.put(`/folders/${id}/role-permissions/propagate`, data),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  checkDuplicates: (data) => api.post('/documents/check-duplicates', data),
  upload: (formData, onUploadProgress) =>
    api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  bulkUpload: (formData, onUploadProgress) =>
    api.post('/documents/upload/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  download: (id) =>
    api.get(`/documents/${id}/download`, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*',
      }
    }),
  getVersions: (id) => api.get(`/documents/${id}/versions`),
  downloadVersion: (docId, versionId) =>
    api.get(`/documents/${docId}/versions/${versionId}/download`, {
      responseType: 'blob',
      headers: {
        'Accept': '*/*',
      }
    }),
  uploadVersion: (id, formData) =>
    api.post(`/documents/${id}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  restoreVersion: (docId, versionId, data) =>
    api.post(`/documents/${docId}/versions/${versionId}/restore`, data),
  getPermissions: (id) => api.get(`/documents/${id}/permissions`),
  grantPermission: (id, data) => api.post(`/documents/${id}/permissions`, data),
  revokePermission: (id, userId) => api.delete(`/documents/${id}/permissions/${userId}`),
  getRolePermissions: (id) => api.get(`/documents/${id}/role-permissions`),
  setRolePermissions: (id, data) => api.put(`/documents/${id}/role-permissions`, data),
  reprocessAI: (id) => api.post(`/documents/${id}/reprocess`),
};

// Financial API
export const financialAPI = {
  getTransactions: (params) => api.get('/financial/transactions', { params }),
  getTransaction: (id) => api.get(`/financial/transactions/${id}`),
  createTransaction: (data) => api.post('/financial/transactions', data),
  getVendors: () => api.get('/financial/vendors'),
  createVendor: (data) => api.post('/financial/vendors', data),
  getCustomers: () => api.get('/financial/customers'),
  createCustomer: (data) => api.post('/financial/customers', data),
  getDashboardStats: () => api.get('/financial/dashboard-stats'),
  getReconciliations: (params) => api.get('/financial/reconciliations', { params }),
  getReconciliation: (id) => api.get(`/financial/reconciliations/${id}`),
  createReconciliation: (data) => api.post('/financial/reconciliations', data),
  updateReconciliation: (id, data) => api.put(`/financial/reconciliations/${id}`, data),
};

// Search API
export const searchAPI = {
  search: (params) => api.get('/search', { params }),
  advancedSearch: (data) => api.post('/search/advanced', data),
};

// Chatbot API
export const chatbotAPI = {
  query: (data) => api.post('/chatbot/query', data, { timeout: 300000 }), // 5 minute timeout for long-running queries
  poll: (conversationId) => api.get(`/chatbot/conversations/${conversationId}/poll`),
  getConversations: (includeDeleted = false) =>
    api.get('/chatbot/conversations', { params: { include_deleted: includeDeleted } }),
  getMessages: (id) => api.get(`/chatbot/conversations/${id}/messages`),
  softDelete: (conversationIds) => api.post('/chatbot/conversations/soft-delete', { conversation_ids: conversationIds }),
  restore: (conversationIds) => api.post('/chatbot/conversations/restore', { conversation_ids: conversationIds }),
  deleteConversation: (id) => api.delete(`/chatbot/conversations/${id}`),
};

// Audit API
export const auditAPI = {
  getLogs: (params) => api.get('/admin/audit-log', { params }),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getSystemHealth: () => api.get('/admin/system-health'),

  // Settings
  getSettings: (includeSensitive = true) => api.get('/admin/settings', { params: { include_sensitive: includeSensitive } }),
  getSetting: (key) => api.get(`/admin/settings/${key}`),
  getPublicSettings: () => api.get('/admin/public-settings'),
  getPublicSetting: (key) => api.get(`/admin/public-settings/${key}`),
  updateSetting: (id, data) => api.put(`/admin/settings/${id}`, data),

  // SMTP/Email
  testSmtp: (config) => api.post('/admin/test-smtp', config),
  testEmail: (email) => api.post('/admin/test-email', { email }),
  getEmailQueueStatus: () => api.get('/admin/email-queue-status'),
  processEmailQueue: () => api.post('/admin/email-queue-process'),
  getSmtpStatus: () => api.get('/admin/smtp-status'),

  // Security Events
  getSecurityEvents: (params) => api.get('/admin/security/events', { params }),
  resolveSecurityEvent: (id) => api.put(`/admin/security/events/${id}/resolve`),
  getFailedLogins: (params) => api.get('/admin/security/failed-logins', { params }),
  getLockouts: (params) => api.get('/admin/security/lockouts', { params }),
  unlockAccount: (id) => api.post(`/admin/security/lockouts/${id}/unlock`),

  // User Activity
  getUserActivity: (params) => api.get('/admin/users/activity', { params }),

  // Company Logo
  uploadLogo: (formData) => api.post('/admin/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getCompanyLogo: () => api.get('/admin/company-logo'),
};

// CSV Extraction API
export const csvAPI = {
  extract: (formData) =>
    api.post('/csv/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  extractCustom: (data) => api.post('/csv/extract-custom', data),
  getExtractions: (documentId) =>
    api.get('/csv/extractions', { params: documentId ? { document_id: documentId } : {} }),
  download: (extractionId) => api.get(`/csv/download/${extractionId}`),
  saveTemplate: (formData) =>
    api.post('/csv/templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getTemplates: () => api.get('/csv/templates'),
  deleteTemplate: (templateId) => api.delete(`/csv/templates/${templateId}`),
};

// Actions API
export const actionsAPI = {
  getMyActions: (params) => api.get('/actions/my-actions', { params }),
  getMyStats: () => api.get('/actions/my-stats'),
  respond: (id, data) => api.post(`/actions/${id}/respond`, data),
  bulkRespond: (data) => api.post('/actions/bulk-respond', data),
  create: (data) => api.post('/actions', data),
  bulkCreate: (data) => api.post('/actions/bulk-create', data),
};

// SharePoint API
export const sharepointAPI = {
  // Connection Management (Application permissions - no OAuth redirect needed)
  connect: () => api.post('/sharepoint/connect'),
  getConnections: () => api.get('/sharepoint/connections'),
  disconnect: (connectionId) => api.delete(`/sharepoint/connections/${connectionId}`),

  // Site & Drive Browsing
  getSites: (connectionId, searchQuery = '') =>
    api.get(`/sharepoint/connections/${connectionId}/sites`, {
      params: searchQuery ? { search: searchQuery } : {}
    }),
  getDrives: (connectionId, siteId) =>
    api.get(`/sharepoint/connections/${connectionId}/sites/${siteId}/drives`),
  getFolderContents: (connectionId, driveId, folderId = null) =>
    api.get(`/sharepoint/connections/${connectionId}/drives/${driveId}/folders${folderId ? `/${folderId}` : ''}`),

  // Auto-Discovery
  autoDiscover: (connectionId, siteIds) =>
    api.post(`/sharepoint/connections/${connectionId}/auto-discover`, { siteIds }),

  // Sync Operations
  saveSelection: (connectionId, selectedSites) =>
    api.post(`/sharepoint/connections/${connectionId}/save-selection`, { selectedSites }),
  startSync: (connectionId, selectedSites) =>
    api.post(`/sharepoint/connections/${connectionId}/sync`, { selectedSites }),
  getSyncStatus: (connectionId) =>
    api.get(`/sharepoint/connections/${connectionId}/sync/status`),
  getDocuments: (connectionId, params = {}) =>
    api.get(`/sharepoint/connections/${connectionId}/documents`, { params }),

  // Search
  search: (params) => api.get('/sharepoint/search', { params }),

  // Process (Download & Create Local Copy)
  processFile: (data) => api.post('/sharepoint/process', data),
};

// Exchange API
export const exchangeAPI = {
  // Connection Management
  connect: () => api.post('/exchange/connect'),
  getConnections: () => api.get('/exchange/connections'),
  disconnect: (connectionId) => api.delete(`/exchange/connections/${connectionId}`),

  // Mailbox Browsing
  getMailboxes: (connectionId, searchQuery = '') =>
    api.get(`/exchange/connections/${connectionId}/mailboxes`, {
      params: searchQuery ? { search: searchQuery } : {}
    }),

  // Save selected mailboxes
  saveMailboxes: (connectionId, selectedMailboxes) =>
    api.post(`/exchange/connections/${connectionId}/save-mailboxes`, { selectedMailboxes }),

  // Search
  search: (params) => api.get('/exchange/search', { params }),

  // Message Details
  getMessageDetails: (mailboxId, messageId) =>
    api.get(`/exchange/messages/${mailboxId}/${messageId}`),

  // Download message as .eml — subject is optional, saves a Graph round-trip when known
  downloadMessage: (mailboxId, messageId, subject) => {
    const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
    return api.get(`/exchange/messages/${mailboxId}/${messageId}/download${params}`, {
      responseType: 'blob'
    });
  },

  // Attachments
  getAttachments: (mailboxId, messageId) =>
    api.get(`/exchange/messages/${mailboxId}/${messageId}/attachments`),
  downloadAttachment: (mailboxId, messageId, attachmentId) =>
    api.get(`/exchange/messages/${mailboxId}/${messageId}/attachments/${attachmentId}/download`, {
      responseType: 'blob'
    }),
};

// Supplier Recon API
export const supplierReconAPI = {
  getConfig: () => api.get('/supplier-recon/config'),
  saveConfig: (data) => api.put('/supplier-recon/config', data),
  // Groups
  getGroups: () => api.get('/supplier-recon/groups'),
  createGroup: (data) => api.post('/supplier-recon/groups', data),
  updateGroup: (id, data) => api.put(`/supplier-recon/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/supplier-recon/groups/${id}`),
  // Supplier aliases (learning system)
  getAliases: (groupId) => api.get(`/supplier-recon/groups/${groupId}/aliases`),
  createAlias: (groupId, data) => api.post(`/supplier-recon/groups/${groupId}/aliases`, data),
  updateAlias: (groupId, aliasId, data) => api.put(`/supplier-recon/groups/${groupId}/aliases/${aliasId}`, data),
  deleteAlias: (groupId, aliasId) => api.delete(`/supplier-recon/groups/${groupId}/aliases/${aliasId}`),

  // Set of accounts per group
  getAccounts: (groupId) => api.get(`/supplier-recon/groups/${groupId}/accounts`),
  createAccount: (groupId, data) => api.post(`/supplier-recon/groups/${groupId}/accounts`, data),
  updateAccount: (groupId, accountId, data) => api.put(`/supplier-recon/groups/${groupId}/accounts/${accountId}`, data),
  deleteAccount: (groupId, accountId) => api.delete(`/supplier-recon/groups/${groupId}/accounts/${accountId}`),
  clearAccounts: (groupId) => api.delete(`/supplier-recon/groups/${groupId}/accounts?confirm=true`),
  previewAccountImport: (groupId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/supplier-recon/groups/${groupId}/accounts/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  commitAccountImport: (groupId, mode, rows) =>
    api.post(`/supplier-recon/groups/${groupId}/accounts/import/commit`, { mode, rows }, { timeout: 60000 }),
  downloadAccountTemplate: (groupId) =>
    api.get(`/supplier-recon/groups/${groupId}/accounts/template`, { responseType: 'blob' }),
  // Browse SharePoint subfolders (deep drill-down)
  getSharePointFolders: (connectionId, driveId, folderId = null) =>
    api.get(`/supplier-recon/sharepoint/${connectionId}/drives/${driveId}/folders${folderId ? `/${folderId}` : ''}`),
  // Browse Exchange mail folders (deep drill-down)
  getMailFolders: (connectionId, mailboxId, folderId = null) =>
    api.get(`/supplier-recon/exchange/${connectionId}/mailboxes/${mailboxId}/folders${folderId ? `/${folderId}` : ''}`),
  // Browse local Doxolve folders (deep drill-down)
  getDoxolveFolders: (folderId = null) =>
    api.get(`/supplier-recon/doxolve/folders${folderId ? `/${folderId}` : ''}`),
};

// Supplier Recon Sessions API
export const supplierReconSessionsAPI = {
  getSessions: () => api.get('/supplier-recon/sessions'),
  searchSessionIds: (q, config) => api.get('/supplier-recon/sessions/search-ids', { params: { q }, ...config }),
  createSession: (data) => api.post('/supplier-recon/sessions', data),
  getSession: (id) => api.get(`/supplier-recon/sessions/${id}`),
  deleteSession: (id) => api.delete(`/supplier-recon/sessions/${id}`),
  runMatching: (id) => api.post(`/supplier-recon/sessions/${id}/run-matching`),
  rerunMatching: (id) => api.post(`/supplier-recon/sessions/${id}/run-matching?reset=true`),
  getReport: (id) => api.get(`/supplier-recon/sessions/${id}/report`),
  downloadDocument: (docId) => api.get(`/supplier-recon/documents/${docId}/download`, { responseType: 'blob', timeout: 60000 }),
  downloadDocumentForSave: (docId) => api.get(`/supplier-recon/documents/${docId}/download?download=1`, { responseType: 'blob', timeout: 60000 }),
  syncSession: (id, mappingIndex = 0, total = 1) => api.post(`/supplier-recon/sessions/${id}/sync`, { mappingIndex, total }, { timeout: 60000 }),
  processSession: (id) => api.post(`/supplier-recon/sessions/${id}/process`, {}, { timeout: 60000 }),
  markError: (id, message) => api.post(`/supplier-recon/sessions/${id}/mark-error`, { message }),
  reprocessDocument: (docId) => api.post(`/supplier-recon/documents/${docId}/reprocess`, {}, { timeout: 60000 }),
  reprocessAll: (sessionId) => api.post(`/supplier-recon/sessions/${sessionId}/reprocess-all`),
  updateDocument: (docId, data) => api.patch(`/supplier-recon/documents/${docId}`, data),
  updateMatch: (matchId, data) => api.patch(`/supplier-recon/matches/${matchId}`, data),
  acceptRecon: (id) => api.post(`/supplier-recon/sessions/${id}/accept-recon`),
  reopenRecon: (id) => api.post(`/supplier-recon/sessions/${id}/reopen-recon`),
  uploadDocuments: (sessionId, files) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    return api.post(`/supplier-recon/sessions/${sessionId}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

// Business Intelligence Module API
export const businessIntelligenceAPI = {
  getConfig: () => api.get('/business-intelligence/config'),
  saveConfig: (data) => api.put('/business-intelligence/config', data),
  // Groups
  getGroups: () => api.get('/business-intelligence/groups'),
  createGroup: (data) => api.post('/business-intelligence/groups', data),
  updateGroup: (id, data) => api.put(`/business-intelligence/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/business-intelligence/groups/${id}`),
  // Folder browsing
  getSharePointFolders: (connectionId, driveId, folderId = null) =>
    api.get(`/business-intelligence/sharepoint/${connectionId}/drives/${driveId}/folders${folderId ? `/${folderId}` : ''}`),
  getMailFolders: (connectionId, mailboxId, folderId = null) =>
    api.get(`/business-intelligence/exchange/${connectionId}/mailboxes/${mailboxId}/folders${folderId ? `/${folderId}` : ''}`),
  getDoxolveFolders: (folderId = null) =>
    api.get(`/business-intelligence/doxolve/folders${folderId ? `/${folderId}` : ''}`),
  // Sessions
  getSessions: () => api.get('/business-intelligence/sessions'),
  createSession: (data) => api.post('/business-intelligence/sessions', data),
  getSession: (id) => api.get(`/business-intelligence/sessions/${id}`),
  deleteSession: (id) => api.delete(`/business-intelligence/sessions/${id}`),
  syncSession: (id, mappingIndex) => api.post(`/business-intelligence/sessions/${id}/sync`, { mappingIndex }),
  processSession: (id) => api.post(`/business-intelligence/sessions/${id}/process`),
  acceptSession: (id) => api.post(`/business-intelligence/sessions/${id}/accept`),
  promoteToWip: (id) => api.post(`/business-intelligence/sessions/${id}/promote-to-wip`),
  markSessionError: (id, message) => api.post(`/business-intelligence/sessions/${id}/mark-error`, { message }),
  resyncSession: (id) => api.post(`/business-intelligence/sessions/${id}/resync`),
  reprocessSession: (id) => api.post(`/business-intelligence/sessions/${id}/reprocess`),
  exportSession: (id, reset = false) => api.post(`/business-intelligence/sessions/${id}/export${reset ? '?reset=1' : ''}`),
  generateReport: (id) => api.post(`/business-intelligence/sessions/${id}/report`),
  generateMissingDocsReport: (id) => api.post(`/business-intelligence/sessions/${id}/missing-docs-report`),
  getSessionDocuments: (id, offset = 0) => api.get(`/business-intelligence/sessions/${id}/documents?offset=${offset}`),
  getAllSessionDocuments: (id) => api.get(`/business-intelligence/sessions/${id}/documents/all`),
  updateDocument: (docId, data) => api.patch(`/business-intelligence/documents/${docId}`, data),
  getDocumentDownloadUrl: (docId, inline = false) =>
    `${api.defaults.baseURL}/business-intelligence/documents/${docId}/download${inline ? '?inline=1' : ''}`,
  // Server-side schedulers
  getSchedulers: () => api.get('/business-intelligence/schedulers'),
  createScheduler: (data) => api.post('/business-intelligence/schedulers', data),
  updateScheduler: (id, data) => api.put(`/business-intelligence/schedulers/${id}`, data),
  deleteScheduler: (id) => api.delete(`/business-intelligence/schedulers/${id}`),
  runScheduler: (id) => api.post(`/business-intelligence/schedulers/${id}/run`),
  previewScheduler: (id) => api.post(`/business-intelligence/schedulers/${id}/preview`),
  backfillScheduler: (id, days) => api.post(`/business-intelligence/schedulers/${id}/backfill`, { days }),
};

// Suppliers (Permanent Supplier Registry) API
export const suppliersAPI = {
  list: (groupId) => api.get('/supplier-recon/suppliers', { params: groupId ? { group_id: groupId } : {} }),
  get: (id) => api.get(`/supplier-recon/suppliers/${id}`),
  create: (data) => api.post('/supplier-recon/suppliers', data),
  update: (id, data) => api.put(`/supplier-recon/suppliers/${id}`, data),
  delete: (id, hard = false) => api.delete(`/supplier-recon/suppliers/${id}`, { params: hard ? { hard: 'true' } : {} }),
  addAlias: (id, alias) => api.post(`/supplier-recon/suppliers/${id}/aliases`, { alias }),
  deleteAlias: (id, aliasId) => api.delete(`/supplier-recon/suppliers/${id}/aliases/${aliasId}`),
  promote: (matchId, groupId) => api.post('/supplier-recon/suppliers/promote', { match_id: matchId, group_id: groupId }),
  autoCapture: (sessionId, forceConflicts = []) => api.post('/supplier-recon/suppliers/auto-capture', { session_id: sessionId, force_conflicts: forceConflicts }),
};

export const reconShareAPI = {
  listTokens: (sessionId) => api.get(`/supplier-recon/sessions/${sessionId}/share-tokens`),
  createToken: (sessionId, data) => api.post(`/supplier-recon/sessions/${sessionId}/share-tokens`, data),
  revokeToken: (sessionId, tokenId) => api.delete(`/supplier-recon/sessions/${sessionId}/share-tokens/${tokenId}`),
  tokenPaymentCSV: (sessionId, tokenId) => api.get(`/supplier-recon/sessions/${sessionId}/share-tokens/${tokenId}/payment-csv`),
};

// Public API — no auth token, separate axios instance (no interceptors that inject auth headers)
const publicApi = axios.create({ baseURL: API_URL });

export const publicReconAPI = {
  load: (token) => publicApi.get(`/public/recon/${token}`),
  updateMatch: (token, matchId, data) => publicApi.patch(`/public/recon/${token}/matches/${matchId}`, data),
  accept: (token, acceptorName) => publicApi.post(`/public/recon/${token}/accept`, { acceptor_name: acceptorName }),
  downloadDocUrl: (token, docId) => `${API_URL}/public/recon/${token}/documents/${docId}/download`,
};

// Supplier Recon Accounting tab API (links transactions to the group's Set of Accounts)
export const supplierReconAccountingAPI = {
  get: (sessionId) => api.get(`/supplier-recon/sessions/${sessionId}/accounting`, { timeout: 60000 }),
  autoAssign: (sessionId, cursor) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/auto-assign`, cursor ? { cursor } : {}, { timeout: 60000 }),
  assign: (sessionId, txnKeys, accountId, learn = false) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/assign`, { txnKeys, accountId, learn }),
  unassign: (sessionId, txnKeys) =>
    api.delete(`/supplier-recon/sessions/${sessionId}/accounting/assign`, { data: { txnKeys } }),
  applySupplier: (sessionId, data) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/apply-supplier`, data),
  confirm: (sessionId, data) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/confirm`, data),
  setSupplierSource: (sessionId, supplierName, source) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/supplier-source`, { supplierName, source }),
  reset: (sessionId) => api.post(`/supplier-recon/sessions/${sessionId}/accounting/reset`),
  relink: (sessionId, txnKeys) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/relink`, { txnKeys }),
  exportCsv: (sessionId) =>
    api.get(`/supplier-recon/sessions/${sessionId}/accounting/export.csv`, { responseType: 'blob', timeout: 60000 }),
  listDefaults: (groupId) => api.get(`/supplier-recon/groups/${groupId}/accounting/defaults`),
  saveDefault: (groupId, data) => api.put(`/supplier-recon/groups/${groupId}/accounting/defaults`, data),
  deleteDefault: (groupId, id) => api.delete(`/supplier-recon/groups/${groupId}/accounting/defaults`, { params: { id } }),
  // Variants that accept an axios request config (e.g. { timeout }) for per-row / bulk saves
  assignWith: (sessionId, txnKeys, accountId, learn, config) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/assign`, { txnKeys, accountId, learn: !!learn }, config),
  unassignWith: (sessionId, txnKeys, config) =>
    api.delete(`/supplier-recon/sessions/${sessionId}/accounting/assign`, { ...config, data: { txnKeys } }),
  confirmWith: (sessionId, data, config) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/confirm`, data, config),
  // VAT types + Accounting Import File (XLSX)
  setTaxType: (sessionId, txnKeys, taxType, config) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/tax-type`, { txnKeys, taxType }, { timeout: 30000, ...config }),
  setSupplierTaxType: (sessionId, supplierName, taxType, clearRowOverrides = false, config) =>
    api.put(`/supplier-recon/sessions/${sessionId}/accounting/supplier-tax-type`, { supplierName, taxType, clearRowOverrides: !!clearRowOverrides }, { timeout: 30000, ...config }),
  getExportSummary: (sessionId) =>
    api.get(`/supplier-recon/sessions/${sessionId}/accounting/export-summary`, { timeout: 60000 }),
  downloadImportFile: (sessionId) =>
    api.get(`/supplier-recon/sessions/${sessionId}/accounting/export.xlsx`, { responseType: 'blob', timeout: 60000 }),
  // Redo accounting (accounting tables only; the recon itself is never touched)
  unconfirmAll: (sessionId, config) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/unconfirm-all`, {}, { timeout: 30000, ...config }),
  clearAll: (sessionId, config) =>
    api.post(`/supplier-recon/sessions/${sessionId}/accounting/clear-all`, { confirm: true }, { timeout: 30000, ...config }),
};

export default api;

